import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import bannerImage from "../assets/login/banner.png";
import styles from "./LoginPage.module.css";
const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const { login } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = "Vui lòng nhập email";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email không hợp lệ";
    }

    if (!password) {
      newErrors.password = "Vui lòng nhập mật khẩu";
    } else if (password.length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error khi user đang gõ
    if (errors.email) {
      setErrors({ ...errors, email: undefined });
    }
  };

  const handleEmailBlur = () => {
    // Validate email khi blur
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      setErrors({ ...errors, email: "Email không hợp lệ" });
    } else if (errors.email && email && /\S+@\S+\.\S+/.test(email)) {
      setErrors({ ...errors, email: undefined });
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors({ ...errors, password: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({}); // Clear previous errors

    try {
      const result = await login(email, password);
      if (
        result &&
        typeof result === "object" &&
        "requiresTwoFactorSetup" in result &&
        result.requiresTwoFactorSetup
      ) {
        navigate(`/setup-2fa?userId=${result.userId}&enforced=true`, {
          replace: true,
        });
        return;
      }

      if (
        result &&
        typeof result === "object" &&
        "requiresTwoFactor" in result &&
        result.requiresTwoFactor
      ) {
        // Redirect đến trang xác thực 2FA
        navigate(`/verify-2fa?userId=${result.userId}`, { replace: true });
        return;
      }
      navigate("/services", { replace: true });
    } catch (error: any) {
      // Xử lý nhiều format error khác nhau
      let errorMessage = "Đăng nhập thất bại. Vui lòng thử lại.";

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      // Hiển thị lỗi ở cả email và password field nếu không rõ
      if (error?.response?.status === 401) {
        setErrors({ password: errorMessage });
      } else {
        setErrors({ password: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      {/* Left Section - Login Form */}
      <div className={styles.loginLeft}>
        <div className={styles.leftCard}>
          <div className={styles.formWrapper}>
            <h1 className={styles.formTitle}>Đăng nhập hệ thống</h1>
            <p className={styles.formDesc}>
              Quản lý toàn bộ doanh nghiệp của bạn trên một nền tảng thống nhất.
            </p>

            <form className={styles.loginForm} onSubmit={handleSubmit}>
              <label className={styles.loginLabel}>Email</label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputContainer}>
                  <Mail className={styles.inputIcon} size={20} />
                  <input
                    type="email"
                    placeholder="user@email.com"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    autoComplete="email"
                    className={`${styles.loginInput} ${
                      errors.email ? styles.inputError : ""
                    }`}
                    disabled={loading}
                  />
                </div>
                {errors.email && (
                  <div className={styles.validationErrorMessage}>
                    {errors.email}
                  </div>
                )}
              </div>

              <label className={styles.loginLabel}>Mật khẩu</label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputContainer}>
                  <Lock className={styles.inputIcon} size={20} />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Nhập mật khẩu"
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="current-password"
                    className={`${styles.loginInput} ${
                      errors.password ? styles.inputError : ""
                    }`}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  >
                    {showPassword ? (
                      <EyeOff className={styles.eyeIcon} size={20} />
                    ) : (
                      <Eye className={styles.eyeIcon} size={20} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <div className={styles.validationErrorMessage}>
                    {errors.password}
                  </div>
                )}
              </div>

              <div className={styles.forgotPasswordRow}>
                <a
                  href="#"
                  className={styles.forgotLink}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/forgot-password");
                  }}
                >
                  Quên mật khẩu?
                </a>
              </div>

              <button
                type="submit"
                className={styles.loginBtn}
                disabled={loading}
              >
                {loading ? "Đang xử lý..." : "Đăng nhập"}
              </button>
            </form>

            <div className={styles.copyright}>
              © 2026 Sea Dragon Technology
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Banner */}
      <div className={styles.loginRight}>
        <div className={styles.bannerWrapper}>
          <img
            src={bannerImage}
            alt="Nền tảng quản lý doanh nghiệp toàn diện"
            className={styles.bannerImage}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
